import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Flex } from 'core/components';
import { formatDateTime } from 'core/util/dateUtils';
import { ProductSkeleton } from 'core/components/Skeletons';
import { ChatContainer, MessageItem } from './KaizenChatSession.styles';
import { UserMessage, AgentMessage, ThinkingIndicator } from './components';

@observer
class KaizenChatSession extends Component {
  chatContainerRef = React.createRef();

  latestUserMessageRef = React.createRef();

  state = {
    stepReasoningOpenState: {}
  };

  constructor(props) {
    super(props);

    // Bind methods to avoid creating new functions on each render
    this.handleToggleReasoning = this.handleToggleReasoning.bind(this);
    this.handleSelectToolResult = this.handleSelectToolResult.bind(this);
    this.formatDate = this.formatDate.bind(this);
  }

  componentDidMount() {
    this.scrollToBottom();
    const { chatSessionModel } = this.props;
    if (chatSessionModel) {
      chatSessionModel.onAgentStep = this.handleAgentStep;
    }
  }

  componentWillUnmount() {
    const { chatSessionModel } = this.props;
    if (chatSessionModel) {
      chatSessionModel.onAgentStep = undefined;
    }
  }

  handleAgentStep = () => {
    this.scrollToBottom();
  };

  scrollToBottom = () => {
    const { scrollToBottom } = this.props;
    if (scrollToBottom) {
      setTimeout(() => {
        scrollToBottom();
      }, 40);
    } else if (this.chatContainerRef.current) {
      this.chatContainerRef.current.scrollTop = this.chatContainerRef.current.scrollHeight;
    }
  };

  handleSelectToolResult = (tool) => {
    const { onSelectToolResult } = this.props;
    if (onSelectToolResult) {
      onSelectToolResult(tool);
    }
  };

  handleToggleReasoning = (stepId) => {
    this.setState((prevState) => ({
      stepReasoningOpenState: {
        ...prevState.stepReasoningOpenState,
        [stepId]: !(prevState.stepReasoningOpenState[stepId] ?? true)
      }
    }));
  };

  handleShowAgentDataInSidebar = (turn, steps) => {
    // eslint-disable-next-line no-console
    console.table(turn);
    // eslint-disable-next-line no-console
    console.table(steps);
  };

  formatDate = (dateTime) => formatDateTime(new Date(dateTime), 'MMM DD, YYYY, hh:mm a');

  renderMessage(message) {
    const { stepReasoningOpenState } = this.state;
    const { chatSessionModel, isExistingChatLoading, isCopilotMode } = this.props;
    const messages = chatSessionModel?.get('messages') || [];
    const isLatestUserMessage = messages.indexOf(message) === messages.length - 1 && message.message_text;

    const userMessage = <UserMessage message={message} formatDate={this.formatDate} />;

    const agentMessages = message.agentTurn?.map((turn) => (
      <AgentMessage
        key={`agent-${turn.id}`}
        turn={turn}
        stepReasoningOpenState={stepReasoningOpenState}
        onToggleReasoning={this.handleToggleReasoning}
        onSelectToolResult={this.handleSelectToolResult}
        onShowAgentDataInSidebar={this.handleShowAgentDataInSidebar}
        formatDate={this.formatDate}
        isCopilotMode={isCopilotMode}
        isExistingChatLoading={isExistingChatLoading}
      />
    ));

    return (
      <MessageItem key={message.id} ref={isLatestUserMessage ? this.latestUserMessageRef : null}>
        {userMessage}

        {!agentMessages?.length && (
          <Flex alignItems="center" gap={1} py={1}>
            <ThinkingIndicator text="Just a sec..." />
          </Flex>
        )}

        {agentMessages}
      </MessageItem>
    );
  }

  render() {
    const { chatSessionModel, isCopilotMode, isExistingChatLoading } = this.props;
    const messages = chatSessionModel?.get('messages') || [];

    return (
      <ChatContainer ref={this.chatContainerRef} isCopilotMode={isCopilotMode}>
        {messages.length === 0 && isExistingChatLoading && (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="16px" height="100%" flex={1}>
            <ProductSkeleton width="30%" height="16px" style={{ alignSelf: 'flex-end' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-end' }} />
            <Flex height={50} />
            <ProductSkeleton width="30%" height="16px" style={{ alignSelf: 'flex-start' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-start' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-start' }} />
          </Flex>
        )}

        {messages.map((message) => this.renderMessage(message))}
      </ChatContainer>
    );
  }
}

export default KaizenChatSession;
